import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Grid, Box, Typography, Tabs, Tab, Paper, Slider } from '@mui/material';
import {
    Select,
    LoadingBar,
} from '../../../components/Common';
import MultiSelect from '../../../components/Common/MultiSelect';
import { trakrefBlue, darkTeal } from '../../../color_utils';
import { trakrefFontFamily, trakrefFontFamilyBold } from '../../../utils';
import { handleMultiSelectChange } from '../utils';
import ServiceBasedTable from './ServiceBasedTable';
import AssetLevelTable from './AssetLevelTable';
import EmissionsByLocation from './EmissionsByLocation';
import Routes from '../../../routes';

const ServiceBasedScope1 = () => {
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Filter states
    const currentYear = new Date().getFullYear().toString();
    const [years, setYears] = useState([currentYear]);
    const [regionDivisions, setRegionDivisions] = useState(["-1"]);
    const [classifications, setClassifications] = useState(["-1"]);
    const [locationNames, setLocationNames] = useState(["-1"]);
    const [stateFilters, setStateFilters] = useState(["-1"]);
    const [applicableRegulations, setApplicableRegulations] = useState(["-1"]);
    const [gwpValue, setGwpValue] = useState('AR5');
    const [maxCapacity, setMaxCapacity] = useState(2000); // slider upper bound; set to the account's actual max from the API
    const [chargeCapacityRange, setChargeCapacityRange] = useState([0, 2000]);
    const [sliderValue, setSliderValue] = useState([0, 2000]);
    const [assetStatuses, setAssetStatuses] = useState(["-1"]);
    const [operationalStatuses, setOperationalStatuses] = useState(["-1"]);
    const [assetTypes, setAssetTypes] = useState(["-1"]);
    const [materialTypes, setMaterialTypes] = useState(["-1"]);

    // Year options: current year down to current year - 6 (7 years; earliest = 2020, the DWH data floor)
    const yearOptions = Array.from({ length: 7 }, (_, i) => {
        const y = (parseInt(currentYear) - i).toString();
        return { value: y, name: y };
    });

    // Filter dropdown options (populated from API)
    const [regionDivisionOptions, setRegionDivisionOptions] = useState([]);
    const [classificationOptions, setClassificationOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [applicableRegulationOptions, setApplicableRegulationOptions] = useState([]);
    const [gwpOptions, setGwpOptions] = useState([]);
    const [assetStatusOptions, setAssetStatusOptions] = useState([]);
    const [operationalStatusOptions, setOperationalStatusOptions] = useState([]);
    const [assetTypeOptions, setAssetTypeOptions] = useState([]);
    const [materialTypeOptions, setMaterialTypeOptions] = useState([]);

    // Report data from API
    const [reportData, setReportData] = useState({
        totalEmissionsCo2e: 0,
        serviceBasedRows: [],
        serviceBasedTotalEmissionsLbs: 0,
        serviceBasedTotalEmissionsCo2e: 0,
        assetLevelRows: [],
        assetLevelTotalEmissionsLbs: 0,
        assetLevelTotalEmissionsCo2e: 0,
        emissionsByLocation: [],
        chartData: [],
    });

    const allOption = { value: "-1", name: 'All' };

    // Debounce slider: update visual immediately, debounce the actual filter
    const debounceRef = useRef(null);
    const handleSliderChange = useCallback((e, newValue) => {
        setSliderValue(newValue);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setChargeCapacityRange(newValue);
        }, 500);
    }, []);

    // Year: can't be empty, defaults to current year
    const handleYearChange = (e) => {
        const newValue = e.target.value;
        if (newValue.length === 0) {
            setYears([currentYear]);
        } else {
            setYears(newValue);
        }
    };

    // Fetch available filter options from API
    const fetchFilterOptions = async () => {
        try {
            const response = await fetch(Routes.PowerBI.ServiceBasedScope1Filters());
            const data = await response.json();

            setLocationOptions([allOption, ...data.filter(f => f.property === 'locationName').map(f => ({ value: f.value, name: f.name }))]);
            setMaterialTypeOptions([allOption, ...data.filter(f => f.property === 'materialType').map(f => ({ value: f.value, name: f.name }))]);
            setRegionDivisionOptions([allOption, ...data.filter(f => f.property === 'regionDivision').map(f => ({ value: f.value, name: f.name }))]);
            setStateOptions([allOption, ...data.filter(f => f.property === 'state').map(f => ({ value: f.value, name: f.name }))]);
            setAssetStatusOptions([allOption, ...data.filter(f => f.property === 'assetStatus').map(f => ({ value: f.value, name: f.name }))]);
            setOperationalStatusOptions([allOption, ...data.filter(f => f.property === 'operationalStatus').map(f => ({ value: f.value, name: f.name }))]);
            setAssetTypeOptions([allOption, ...data.filter(f => f.property === 'assetType').map(f => ({ value: f.value, name: f.name }))]);
            setClassificationOptions([allOption, ...data.filter(f => f.property === 'classification').map(f => ({ value: f.value, name: f.name }))]);
            setApplicableRegulationOptions([allOption, ...data.filter(f => f.property === 'applicableRegulation').map(f => ({ value: f.value, name: f.name }))]);

            const gwps = data.filter(f => f.property === 'gwpValue').map(f => ({ id: f.value, label: f.name }));
            setGwpOptions(gwps);

            // Charge Capacity slider bound: use the account's actual max capacity from the API,
            // falling back to 2000 if it's missing/zero. Start the range at the full span.
            const apiMax = parseInt((data.find(f => f.property === 'maxChargeCapacity') || {}).value, 10);
            const capMax = Number.isFinite(apiMax) && apiMax > 0 ? apiMax : 2000;
            setMaxCapacity(capMax);
            setChargeCapacityRange([0, capMax]);
            setSliderValue([0, capMax]);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching filter options:', error);
            setLoading(false);
        }
    };

    // Fetch report data with all current filters
    const fetchReportData = async () => {
        setTableLoading(true);
        try {
            const requestBody = {
                years,
                gwpType: gwpValue,
                regionDivisions,
                classifications,
                locationNames,
                states: stateFilters,
                applicableRegulations,
                chargeCapacityMin: chargeCapacityRange[0] > 0 ? chargeCapacityRange[0] : null,
                chargeCapacityMax: chargeCapacityRange[1] < maxCapacity ? chargeCapacityRange[1] : null,
                assetStatuses,
                operationalStatuses,
                assetTypes,
                materialTypes,
            };

            const response = await fetch(Routes.PowerBI.ServiceBasedScope1(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();
            setReportData({
                totalEmissionsCo2e: data.totalEmissionsCo2e || 0,
                serviceBasedRows: data.serviceBasedRows || [],
                serviceBasedTotalEmissionsLbs: data.serviceBasedTotalEmissionsLbs || 0,
                serviceBasedTotalEmissionsCo2e: data.serviceBasedTotalEmissionsCo2e || 0,
                assetLevelRows: data.assetLevelRows || [],
                assetLevelTotalEmissionsLbs: data.assetLevelTotalEmissionsLbs || 0,
                assetLevelTotalEmissionsCo2e: data.assetLevelTotalEmissionsCo2e || 0,
                emissionsByLocation: data.emissionsByLocation || [],
                chartData: data.chartData || [],
            });
        } catch (error) {
            console.error('Error fetching report data:', error);
        } finally {
            setTableLoading(false);
        }
    };

    // Initial load: fetch filter options once, then fetch report data
    useEffect(() => {
        const load = async () => {
            await fetchFilterOptions();
            fetchReportData();
        };
        load();
    }, []);

    // When any filter changes: fetch report data
    useEffect(() => {
        if (!loading) {
            fetchReportData();
        }
    }, [years, gwpValue, regionDivisions, classifications, locationNames, stateFilters,
        applicableRegulations, chargeCapacityRange, assetStatuses, operationalStatuses,
        assetTypes, materialTypes]);

    if (loading) return <LoadingBar />;

    const formatNumber = (num) => {
        if (num == null) return '0';
        return Math.round(num).toLocaleString();
    };

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Filters Section */}
                <Grid item xs={12} md={9}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Year"
                                    variant="standard"
                                    value={years}
                                    options={yearOptions}
                                    onChange={handleYearChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Region/Division"
                                    variant="standard"
                                    value={regionDivisions}
                                    options={regionDivisionOptions}
                                    onChange={handleMultiSelectChange(setRegionDivisions)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Classification"
                                    variant="standard"
                                    value={classifications}
                                    options={classificationOptions}
                                    onChange={handleMultiSelectChange(setClassifications)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Location"
                                    variant="standard"
                                    value={locationNames}
                                    options={locationOptions}
                                    onChange={handleMultiSelectChange(setLocationNames)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="State"
                                    variant="standard"
                                    value={stateFilters}
                                    options={stateOptions}
                                    onChange={handleMultiSelectChange(setStateFilters)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Applicable Regulation"
                                    variant="standard"
                                    value={applicableRegulations}
                                    options={applicableRegulationOptions}
                                    onChange={handleMultiSelectChange(setApplicableRegulations)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <Select
                                    label="GWP Value"
                                    variant="standard"
                                    value={gwpValue}
                                    options={gwpOptions}
                                    onChange={e => setGwpValue(e.target.value)}
                                    showNullOption={false}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography
                                    variant="caption"
                                    sx={{ color: trakrefBlue, fontFamily: trakrefFontFamilyBold, textTransform: 'uppercase' }}
                                >
                                    Charge Capacity (lbs)
                                </Typography>
                                <Slider
                                    value={sliderValue}
                                    onChange={handleSliderChange}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={maxCapacity}
                                    sx={{ mt: 1, color: trakrefBlue }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption">{sliderValue[0]}</Typography>
                                    <Typography variant="caption">{sliderValue[1]}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Asset Status"
                                    variant="standard"
                                    value={assetStatuses}
                                    options={assetStatusOptions}
                                    onChange={handleMultiSelectChange(setAssetStatuses)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Operational Status"
                                    variant="standard"
                                    value={operationalStatuses}
                                    options={operationalStatusOptions}
                                    onChange={handleMultiSelectChange(setOperationalStatuses)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Asset Type"
                                    variant="standard"
                                    value={assetTypes}
                                    options={assetTypeOptions}
                                    onChange={handleMultiSelectChange(setAssetTypes)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <MultiSelect
                                    label="Material Type"
                                    variant="standard"
                                    value={materialTypes}
                                    options={materialTypeOptions}
                                    onChange={handleMultiSelectChange(setMaterialTypes)}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Total Emissions Card */}
                <Grid item xs={12} md={3}>
                    <Box sx={{
                        p: 2,
                        textAlign: 'center',
                        borderRadius: '5px',
                        background: `linear-gradient(135deg, ${trakrefBlue}, ${darkTeal})`,
                        color: '#fff',
                    }}>
                        <Typography variant="body2" sx={{ fontFamily: trakrefFontFamily, color: '#fff' }}>
                            Total Leaked Emissions (lbs CO2e)
                        </Typography>
                        <Typography variant="h4" sx={{ fontFamily: trakrefFontFamilyBold, color: '#fff', my: 1 }}>
                            {formatNumber(reportData.totalEmissionsCo2e)}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    sx={{
                        '& .MuiTabs-indicator': { backgroundColor: trakrefBlue },
                        '& .MuiButtonBase-root.MuiTab-root': {
                            color: `${trakrefBlue} !important`,
                            fontSize: '13px',
                            fontFamily: trakrefFontFamily,
                            outline: 'none',
                            border: 'none',
                        },
                        '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
                            color: `${trakrefBlue} !important`,
                            fontFamily: trakrefFontFamilyBold,
                        },
                        '& .MuiTabs-flexContainer': { gap: '30px' },
                    }}
                >
                    <Tab label="Service-Based Emissions" />
                    <Tab label="Asset-Level Emissions" />
                    <Tab label="Emissions by Location" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
                <ServiceBasedTable
                    rows={reportData.serviceBasedRows}
                    totalEmissionsLbs={reportData.serviceBasedTotalEmissionsLbs}
                    totalEmissionsCo2e={reportData.serviceBasedTotalEmissionsCo2e}
                    loading={tableLoading}
                />
            )}
            {activeTab === 1 && (
                <AssetLevelTable
                    rows={reportData.assetLevelRows}
                    totalEmissionsLbs={reportData.assetLevelTotalEmissionsLbs}
                    totalEmissionsCo2e={reportData.assetLevelTotalEmissionsCo2e}
                    loading={tableLoading}
                />
            )}
            {activeTab === 2 && (
                <EmissionsByLocation
                    rows={reportData.emissionsByLocation}
                    chartData={reportData.chartData}
                    loading={tableLoading}
                />
            )}
        </Box>
    );
};

export default ServiceBasedScope1;
